import { FC, useEffect, useState } from 'react';
import { Box, Button, Calendar, Notification, NotificationAlignment, NotificationVariants } from '@base-app';
import { ProcessLoader } from '@builder';
import { OnChangeDateCallback } from 'react-calendar';
import { Sizes, Variants } from '@/common/types';
import {
  StyledArrowIcon,
  StyledBackButtonText,
  StyledCalendarContainer,
  StyledCalendarLoaderContainer,
  StyledContainer,
  StyledProcessLoaderContainer,
  StyledSchedulerContainer,
  StyledSpinnerIcon,
  StyledTimeRangeTitle,
  StyledTimeRangesContainer,
  bookApptBtnStyles,
} from './AppointmentScheduler.styles';
import { AppointmentSchedulerProps } from './AppointmentScheduler.types';
import { formatDate } from '@/common/utils';
import { CALENDAR_MONTH_FORMAT, GENETIC_COUNSELING_TRANSLATION } from '../../constants';
import { useTranslation } from 'react-i18next';
import useQueryAvailableTimeRanges from '../../hooks/useQueryAvailableTimeRanges';
import TimeRanges from '../TimeRanges/TimeRanges';
import { COMMON_TRANSLATION } from '@/common/constants';
import { useBookAppointmentContext, useSchedulerInquiryContext } from '../../hooks';
import { EventTypeId, TimeRange } from '../../types';
import { formatISO } from 'date-fns';

const AppointmentScheduler: FC<AppointmentSchedulerProps> = ({
  availableDates,
  isFetchingDates,
  isReschedulerFlow,
  handleBackButtonClick,
  handleMonthChange,
  stateCode,
  timeZone,
  language,
}) => {
  const { t } = useTranslation(GENETIC_COUNSELING_TRANSLATION);
  const [selectedDate, setSetselectedDate] = useState<Date>();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>();
  const bookAppointmentContext = useBookAppointmentContext();
  const schedulerInquiryContext = useSchedulerInquiryContext();

  const {
    data: timeRangesData,
    isFetching,
    isError: isErrorFetchingTimes,
    refetch,
    remove,
  } = useQueryAvailableTimeRanges(
    {
      date: formatDate(selectedDate, 'yyyy-MM-dd'),
      language,
      stateCode,
      timeZone,
    },
    { enabled: false },
  );

  useEffect(() => {
    if (selectedDate !== undefined) void refetch();
  }, [selectedDate]);

  useEffect(() => {
    if (bookAppointmentContext?.isError) {
      setSelectedTimeRange(undefined);
      void refetch();
    }
  }, [bookAppointmentContext?.isError]);

  const handleDaySelected: OnChangeDateCallback = (date, _event) => {
    setSetselectedDate(date);
    setSelectedTimeRange(undefined);
  };

  const handleSelectedTimeRange = (timeRange: TimeRange): void => {
    setSelectedTimeRange(timeRange);
  };

  const handleBackButton = (): void => {
    remove();
    handleBackButtonClick();
  };

  return (
    <>
      {bookAppointmentContext?.isError && (
        <Notification
          css={{ marginBottom: '$8' }}
          description={t('scheduler.appointments_error_notification.description')}
          textAlign={NotificationAlignment.LEFT}
          title={t('scheduler.appointments_error_notification.title')}
          variant={NotificationVariants.ERROR}
        />
      )}
      {isErrorFetchingTimes && (
        <Notification
          css={{ marginBottom: '$8' }}
          textAlign={NotificationAlignment.LEFT}
          title={t('scheduler.times_error_notification.title')}
          variant={NotificationVariants.ERROR}
        />
      )}
      <StyledContainer>
        <Button variant={Variants.TERTIARY} onClick={handleBackButton}>
          <StyledArrowIcon />
          <StyledBackButtonText>{t('scheduler.back_to_scheduling_background')}</StyledBackButtonText>
        </Button>
        <StyledSchedulerContainer>
          <StyledCalendarContainer>
            {isFetchingDates && (
              <StyledCalendarLoaderContainer data-testid="calendar-loader">
                <StyledSpinnerIcon />
              </StyledCalendarLoaderContainer>
            )}
            <Calendar
              className="calendar"
              onChange={handleDaySelected}
              showNeighboringMonth={false}
              onActiveStartDateChange={({ activeStartDate }) =>
                handleMonthChange(formatDate(new Date(activeStartDate), CALENDAR_MONTH_FORMAT))
              }
              tileDisabled={({ date }) =>
                availableDates ? !availableDates?.includes(formatDate(date, 'yyyy-MM-dd')) : true
              }
            />
          </StyledCalendarContainer>
          <StyledTimeRangesContainer>
            {isFetching ? (
              <StyledProcessLoaderContainer>
                <ProcessLoader title={t('scheduler.loading_appointment_times')} />
              </StyledProcessLoaderContainer>
            ) : (
              <>
                <Box css={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <StyledTimeRangeTitle
                    data-testid={selectedDate ? 'appointment-scheduler-selected-date' : 'appointment-scheduler-title'}
                  >
                    {selectedDate
                      ? `${t(`months.${selectedDate.getMonth() + 1}`, {
                          ns: COMMON_TRANSLATION,
                        })} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}`
                      : t('scheduler.select_appointment_date')}
                  </StyledTimeRangeTitle>
                  {(timeRangesData != null || isErrorFetchingTimes) && (
                    <TimeRanges
                      handleSelectedTimeRange={handleSelectedTimeRange}
                      isErrorFetchingTimes={isErrorFetchingTimes}
                      timeRangesData={timeRangesData ?? { availableDatetimes: [] }}
                      timezone={timeZone}
                    />
                  )}
                </Box>
                <Box css={{ marginTop: '$6' }}>
                  {!isErrorFetchingTimes && (
                    <Button
                      css={bookApptBtnStyles}
                      data-testid="book-appointment-button"
                      disabled={selectedDate === undefined || selectedTimeRange === undefined}
                      onClick={() => {
                        if (selectedTimeRange !== undefined && timeZone !== undefined)
                          bookAppointmentContext?.handleBookAppointment?.({
                            event: isReschedulerFlow ? EventTypeId.reschedule : EventTypeId.schedule,
                            eventDateTime: formatISO(new Date()),
                            startTime: selectedTimeRange.startTime,
                            endTime: selectedTimeRange.endTime,
                            acuityAppointmentTypeID: selectedTimeRange.appointmentTypeID,
                            gcProfileID: selectedTimeRange.geneticCounselorProfile.id,
                            timeZone,
                            consentToGeneticCounseling: schedulerInquiryContext?.geneticCounselingConsent,
                            consentToGeneticCounselingAt: schedulerInquiryContext?.consentToGeneticCounselingAt,
                            consentToShareResults: schedulerInquiryContext?.resultsSharingConsent,
                            consentToShareResultsAt: schedulerInquiryContext?.consentToShareResultsAt,
                          });
                      }}
                      size={Sizes.MEDIUM}
                    >
                      {isReschedulerFlow ? t('scheduler.update_appointment_time') : t('scheduler.book_appointment')}
                    </Button>
                  )}
                  {!isErrorFetchingTimes && isReschedulerFlow && (
                    <Button
                      css={{
                        marginTop: '$6',
                        ...bookApptBtnStyles,
                      }}
                      data-testid="book-appointment-button"
                      onClick={bookAppointmentContext?.handleCancelReschedule}
                      size={Sizes.MEDIUM}
                      variant={Variants.SECONDARY}
                    >
                      {t('scheduler.cancel')}
                    </Button>
                  )}
                </Box>
              </>
            )}
          </StyledTimeRangesContainer>
        </StyledSchedulerContainer>
      </StyledContainer>
    </>
  );
};

export default AppointmentScheduler;
